import { useEffect, useMemo, useState } from "react";
import { colors, css, frames, glyphs, mediaQueriesNumbers, useMediaQuery } from "../../../../../Styles";
import { AicoConnection, PageHeader, TableLayout, NewButton, ReactiveTable, LoadingSpinner } from "../../../../../Components";
import { ButtonTypes, ListData_ValueTypes, TableStyles_Types } from "../../../../../Definitions";
import { useReactTable, getCoreRowModel, getSortedRowModel, getFilteredRowModel } from "@tanstack/react-table";
import { useColumnConstructor } from "../../../../../Components/Tables/_Tables/useColumnConstructor";
import { useLazyQuery } from "@apollo/client";
import { DEVICE_CHANGE_QUEUE_INFO, DeviceChangeQueueInfoArgs, DeviceChangeQueueInfoResponse } from "../../../../../Services/Agrisys_GqlDataCaller/ServiceToolCalls/GQLCalls/deviceChangeQueueInfo";
import { useAicoConnection } from "../../../../../Components/ToolsComponents/_ToolsComponents/aicoConnectionContext";
import { useTranslation } from "react-i18next";

const columnsData = [
	{ headerValue: "ID", accessor: "id", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "CPU ID", accessor: "lan_controller_cpu_id", type: ListData_ValueTypes.String, sort: true },
	{ headerValue: "Messages", accessor: "message_count", type: ListData_ValueTypes.String, sort: true },
	{ headerValue: "Oldest message", accessor: "oldest_message_timestamp", type: ListData_ValueTypes.Date, sort: true },
	{ headerValue: "error", accessor: "error_occurred", type: ListData_ValueTypes.Div, sort: true },
]

export default function DeviceChangeQueueList() {
	const { t } = useTranslation();
	const mobileXs = useMediaQuery(`(max-width: ${mediaQueriesNumbers.mobileXs}px)`);
	const limit = 50;

	const [pagination, setPagination] = useState({
		pageIndex: 0, //initial page index
		pageSize: limit,
	});

	const { state: connectionState } = useAicoConnection();

	const [fetchData, { data: data, loading: loading, error: error, }] = useLazyQuery<DeviceChangeQueueInfoResponse, DeviceChangeQueueInfoArgs>(DEVICE_CHANGE_QUEUE_INFO, {
		fetchPolicy: "network-only",
	});

	useEffect(() => {
		if (connectionState.domain) {
			setPagination({ pageIndex: 0, pageSize: limit });
			fetchData({
				variables: {
					domain: connectionState.domain,
					input: {
						fetchAmount: limit + 1,
						offset: 0
					}
				}
			})
		} else {
			setPagination({ pageIndex: 0, pageSize: limit });
			fetchData(); //TODO: find a better way to do this, as this will always give an err?
		}
	}, [connectionState.domain]);

	const tableData = useMemo(() => {
		return data?.controller.deviceChangeQueueInfo.map(e => {
			return {
				...e,
				id: e.lan_controller_id,
				oldest_message_timestamp: e.oldest_message_timestamp && !isNaN(parseInt(e.oldest_message_timestamp)) ? parseInt(e.oldest_message_timestamp) * 1000 : undefined,
				error_occurred: e.error_occurred ? <glyphs.Error fillColor={colors.systemRed} /> : undefined
			}
		}) ?? [];
	}, [data]);

	const columns = useColumnConstructor({
		accessorData: columnsData,
		tableStyle: TableStyles_Types.primaryTable,
		canEdit: false,
	});

	const table = useReactTable({
		columns,
		data: tableData,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		onPaginationChange: setPagination,
		state: {
			pagination,
		},
		manualPagination: true,
		pageCount: tableData.length > limit ? pagination.pageIndex + 2 : pagination.pageIndex + 1,
	});

	return (
		<frames.Page_inner>
			<PageHeader topHeader='Device Data' subHeader='Queues' />
			<frames._page_inner_content>
				<TableLayout
					fillScreenTable
					searchbBarArea={mobileXs ? undefined : <AicoConnection hideLANControllers inTable />}
					buttonArea={<div style={{ display: "flex", gap: "5px" }}>
						{mobileXs && <AicoConnection hideLANControllers inTable />}
						<NewButton
							content={"fetch"}
							buttonType={ButtonTypes.tableButton}
							startEnhancer={glyphs.Fetch({})}
							onClick={() => {
								if (connectionState.domain) {
									fetchData({
										variables: {
											domain: connectionState.domain,
											input: {
												fetchAmount: limit + 1,
												offset: pagination.pageIndex * limit,
											},
										},
									});
								}
							}}
							disabled={loading || !connectionState.domain}
						/>
					</div>}
					tableArea={<>
						{loading && <div style={{ padding: "20px", display: "flex", justifyContent: "center" }}><LoadingSpinner /></div>}
						{(!loading && (error || tableData.length === 0)) && <p style={{ ...css.emptyPen, textAlign: "center", padding: "20px" }}>{t("noData", { defaultValue: "no data" })}</p>}
						{!loading && !error && tableData.length !== 0 &&
							<ReactiveTable
								table={table}
								tableType={TableStyles_Types.primaryTable}
								emptyText="dut"
								renderFooter={false}
								loading={loading}
								minRows={30}
							/>
						}
					</>}
					paginationBtns={{
						onPrev: () => {
							if (table.getCanPreviousPage() && connectionState.domain) {
								fetchData({ variables: { domain: connectionState.domain, input: { fetchAmount: limit + 1, offset: limit * (table.getState().pagination.pageIndex - 1) } } })
								table.previousPage()
							}
						},
						onNext: () => {
							if (table.getCanNextPage() && connectionState.domain) {
								fetchData({ variables: { domain: connectionState.domain, input: { fetchAmount: limit + 1, offset: limit * (table.getState().pagination.pageIndex + 1) } } })
								table.nextPage()
							}
						},
						canNextPage: !table.getCanNextPage(),
						canPreviousPage: !table.getCanPreviousPage(),
						thisPageIndex: table.getState().pagination.pageIndex + 1,
						pageCount: table.getPageCount()
					}}
				/>

			</frames._page_inner_content>
		</frames.Page_inner>
	)
}